import React from 'react';
import { bool, array, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { propTypes } from '../../util/types';
import { Page, LayoutSingleColumn } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import LandingBlocks from './LandingBlocks';

import css from './LandingPage.module.css';

const DEFAULT_SEO = {
  title: 'Domotique et Maison Connectée | Neokasa',
  description:
    'Trouvez et achetez les meilleurs appareils pour la domotique et pour la maison connectée en France.',
};

export const LandingPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    inProgress,
    error,
    seo,
    blocks,
    blogPosts,
    productsByBlock,
    updatedAt,
  } = props;

  const pageSeo = { ...DEFAULT_SEO, ...(seo || {}) };
  const firstHero = (blocks || []).find(b => b.type === 'hero');
  const lcpUrl = firstHero?.slides?.[0]?.imageUrl || firstHero?.slides?.[0]?.imageUrlSm;
  const ogUrl = pageSeo.ogImageUrl || lcpUrl;

  if (inProgress && !(blocks && blocks.length)) {
    return (
      <Page title={pageSeo.title} description={pageSeo.description} scrollingDisabled={false}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.loading}>Chargement…</div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (error && !(blocks && blocks.length)) {
    return (
      <Page title={pageSeo.title} description={pageSeo.description} scrollingDisabled={false}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.error}>
            <h2>Oops, something went wrong!</h2>
            <p>{error.message}</p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page
      title={pageSeo.title}
      description={pageSeo.description}
      scrollingDisabled={false}
      socialSharing={{
        title: pageSeo.title,
        description: pageSeo.description,
        images1200: ogUrl
          ? [{ name: 'facebook', url: ogUrl, width: 1200, height: 630 }]
          : [],
        images600: ogUrl ? [{ name: 'twitter', url: ogUrl, width: 600, height: 314 }] : [],
      }}
      schema={{
        '@type': 'WebPage',
        '@id': 'https://www.neokasa.fr/#webpage',
        name: pageSeo.title,
        description: pageSeo.description,
        url: 'https://www.neokasa.fr/',
        mainEntity: {
          '@type': 'Organization',
          '@id': 'https://www.neokasa.fr/#organization',
          name: 'Neokasa',
          description: 'Leading smart home marketplace',
          url: 'https://www.neokasa.fr/',
          sameAs: [
            'https://www.facebook.com/neokasa',
            'https://twitter.com/neokasa',
            'https://www.instagram.com/neokasa',
          ],
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'FR',
            addressLocality: 'France',
          },
          areaServed: {
            '@type': 'Country',
            name: 'France',
          },
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.neokasa.fr/',
            },
          ],
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://www.neokasa.fr/products?q={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      }}
      openGraphType="website"
      published="2025-01-01"
      updated={updatedAt || '2025-08-12'}
    >
      {lcpUrl ? (
        <Helmet>
          <link rel="preload" as="image" href={lcpUrl} fetchpriority="high" />
        </Helmet>
      ) : null}
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <LandingBlocks
          blocks={blocks}
          blogPosts={blogPosts}
          productsByBlock={productsByBlock}
          config={config}
          intl={intl}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.propTypes = {
  inProgress: bool,
  error: propTypes.error,
  seo: object,
  blocks: array,
  blogPosts: array,
  productsByBlock: object,
  updatedAt: string,
};

const mapStateToProps = state => {
  const {
    inProgress,
    error,
    seo,
    blocks,
    blogPosts,
    listingIdsByBlock,
    updatedAt,
  } = state.LandingPage || {};

  const productsByBlock = {};
  Object.entries(listingIdsByBlock || {}).forEach(([blockId, ids]) => {
    const refs = (ids || []).map(id => ({
      id: typeof id === 'string' ? { uuid: id, _sdkType: 'UUID' } : id,
      type: 'listing',
    }));
    productsByBlock[blockId] = refs.length ? getMarketplaceEntities(state, refs) : [];
  });

  return {
    inProgress: !!inProgress,
    error: error || null,
    seo: seo || null,
    blocks: blocks || [],
    blogPosts: blogPosts || [],
    productsByBlock,
    updatedAt: updatedAt || null,
  };
};

const LandingPage = compose(
  withRouter,
  connect(mapStateToProps)
)(LandingPageComponent);

export default LandingPage;
